import type {ColumnSort} from "./useColumnLayout";

export type ColumnSortValue = string | number | boolean | null | undefined;
const collator = new Intl.Collator(undefined, {numeric: true, sensitivity: "base"});
const isBlank = (value: ColumnSortValue) =>
  value == null ||
  (typeof value === "number" && !Number.isFinite(value)) ||
  (typeof value === "string" && value.trim() === "");

/** Stable source indices for scalar columns. Missing values stay last in both directions. */
export function getColumnSortedIndices<T>(
  rows: readonly T[],
  getValue: (row: T) => ColumnSortValue,
  direction: ColumnSort["direction"]
): number[] {
  const values = rows.map(getValue);
  return rows
    .map((_, index) => index)
    .sort((a, b) => {
      const left = values[a],
        right = values[b];
      const leftBlank = isBlank(left),
        rightBlank = isBlank(right);
      if (leftBlank || rightBlank) return leftBlank === rightBlank ? a - b : leftBlank ? 1 : -1;
      const comparison =
        typeof left === "number" && typeof right === "number"
          ? left === right
            ? 0
            : left < right
            ? -1
            : 1
          : typeof left === "boolean" && typeof right === "boolean"
          ? Number(left) - Number(right)
          : collator.compare(String(left), String(right));
      return comparison === 0 ? a - b : direction === "desc" ? -comparison : comparison;
    });
}
